import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Doctor, DoctorService, DoctorSummary } from '../../../core/services/doctor.service';

@Component({
  selector: 'app-doctor-search',
  imports: [CommonModule, FormsModule],
  templateUrl: './doctor-search.component.html',
  styleUrl: './doctor-search.component.css',
})
export class DoctorSearchComponent implements OnInit, OnDestroy {
  @Output() doctorSelected = new EventEmitter<Doctor>();

  private readonly CODE = /^DR-[A-Z0-9]{6}$/i;          // full doctor code: DR-XXXXXX

  query = '';
  results: DoctorSummary[] = [];
  loading = false;
  selecting = false;                                    // loading the chosen doctor's profile + slots
  error = false;
  open = false;
  activeIndex = -1;

  private loaded = new Map<string, Doctor>();           // full profiles already fetched by code lookup
  private typing$ = new Subject<string>();
  private now$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private doctors: DoctorService, private host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    const typed$ = this.typing$.pipe(debounceTime(300), distinctUntilChanged());
    merge(typed$, this.now$).pipe(
      tap(() => { this.loading = true; this.error = false; this.open = true; this.activeIndex = -1; }),
      switchMap(q => this.fetch(q).pipe(
        map(list => ({ q, list, failed: false })),
        catchError(() => of({ q, list: [] as DoctorSummary[], failed: true }))
      )),
      takeUntil(this.destroy$)
    ).subscribe(({ q, list, failed }) => {
      if (q.toLowerCase() !== this.query.trim().toLowerCase()) return;   // stale response
      this.loading = false;
      this.error = failed;
      this.results = list;
    });
  }

  /** Full code → exact lookup (real API). Anything else → name search (list). */
  private fetch(q: string) {
    if (this.CODE.test(q)) {
      return this.doctors.lookupDoctor(q.toUpperCase()).pipe(
        map(d => {
          if (!d) return [] as DoctorSummary[];
          this.loaded.set(d.doctor_code.toUpperCase(), d);
          return [{ doctor_code: d.doctor_code, name: d.name, specialty: d.specialty, hospital: d.hospital, profilepic_url: d.profilepic_url }];
        })
      );
    }
    return this.doctors.searchDoctorsByName(q);
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  @HostListener('document:click', ['$event'])
  onDocClick(e: Event): void {
    if (!this.host.nativeElement.contains(e.target as Node)) this.open = false;
  }

  onQueryChange(v: string): void {
    this.query = v;
    const q = v.trim();
    this.results = []; this.error = false;
    if (q.length < 2) { this.open = false; this.loading = false; return; }
    this.typing$.next(q);
  }

  onFocus(): void {
    if (this.query.trim().length >= 2 && !this.loading) this.open = true;
  }

  /** Search button / Enter with nothing highlighted → search immediately */
  searchNow(): void {
    const q = this.query.trim();
    if (q.length < 2) return;
    this.now$.next(q);
  }

  onKeydown(e: KeyboardEvent): void {
    const input = e.target as HTMLInputElement;
    if (e.key === ' ' && input.selectionStart === 0) { e.preventDefault(); return; }   // same rule as the appointment search box

    if (e.key === 'ArrowDown' && this.results.length) {
      e.preventDefault(); this.open = true;
      this.activeIndex = (this.activeIndex + 1) % this.results.length;
    } else if (e.key === 'ArrowUp' && this.results.length) {
      e.preventDefault();
      this.activeIndex = (this.activeIndex - 1 + this.results.length) % this.results.length;
    } else if (e.key === 'Enter') {
      e.preventDefault();
      this.activeIndex >= 0 ? this.select(this.results[this.activeIndex]) : this.searchNow();
    } else if (e.key === 'Escape') {
      this.open = false;
    }
  }

  /** Picking a row loads the full profile + slots (if not already loaded) and opens the booking dialog */
  select(d: DoctorSummary): void {
    if (this.selecting) return;
    const cached = this.loaded.get(d.doctor_code.toUpperCase());
    if (cached) { this.finish(d, cached); return; }

    this.selecting = true;
    this.doctors.lookupDoctor(d.doctor_code).pipe(takeUntil(this.destroy$)).subscribe({
      next: full => {
        this.selecting = false;
        if (full) { this.loaded.set(d.doctor_code.toUpperCase(), full); this.finish(d, full); }
        else { this.results = []; this.error = true; }
      },
      error: () => { this.selecting = false; this.results = []; this.error = true; },
    });
  }

  private finish(d: DoctorSummary, full: Doctor): void {
    this.query = d.name;
    this.open = false;
    this.activeIndex = -1;
    this.doctorSelected.emit(full);
  }

  initials(name: string): string {
    const parts = (name || '').replace(/^dr\.?\s+/i, '').trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
  }

  /** Splits text so the matched part can be wrapped in <mark> without innerHTML */
  parts(text: string): { t: string; m: boolean }[] {
    const q = this.query.trim();
    const i = q ? (text || '').toLowerCase().indexOf(q.toLowerCase()) : -1;
    if (i < 0) return [{ t: text, m: false }];
    return [
      { t: text.slice(0, i), m: false },
      { t: text.slice(i, i + q.length), m: true },
      { t: text.slice(i + q.length), m: false },
    ].filter(p => p.t);
  }
}